// src/pages/AdminDashboard.js
import React, { useEffect, useMemo, useState, useCallback } from "react";
import api from "../utils/api";
import {
  FaUsers,
  FaChartPie,
  FaSignOutAlt,
  FaBars,
  FaLink,
  FaBell,
  FaCog,
  FaGift,
  FaPhoneAlt,
  FaFacebook,
  FaWhatsapp,
  FaEnvelope,
  FaChevronLeft,
  FaChevronRight,
  FaPlus,
} from "react-icons/fa";
import AddUserModal from "../components/AddUserModal";

function AdminDashboard() {
  const [users, setUsers] = useState([]);
  const [referrals, setReferrals] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [activeSection, setActiveSection] = useState("dashboard");
  const [sidebarVisible, setSidebarVisible] = useState(true);

  // 🔎 بحث المستخدمين
  const [userSearch, setUserSearch] = useState("");

  // إشعارات
  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");
  const [isGlobal, setIsGlobal] = useState(true);
  const [recipients, setRecipients] = useState([]);
  const [activeNotification, setActiveNotification] = useState(null);
  const [sending, setSending] = useState(false);

  // ✅ تحديث بيانات الأدمن (تم التعديل: phone بدل email)
  const [newPhone, setNewPhone] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [updatingAdmin, setUpdatingAdmin] = useState(false);

  // إدارة خطط الاشتراك
  const [plans, setPlans] = useState([]);
  const [newPlan, setNewPlan] = useState({
    name: "",
    price: "",
    durationDays: "",
    trialDays: "",
  });

  // إعدادات الاشتراك الافتراضية + بيانات التواصل
  const [contactInfo, setContactInfo] = useState({
    subscriptionPrice: "",
    subscriptionDurationDays: "",
    whatsapp: "",
    facebook: "",
    email: "",
  });

  const [loadingContact, setLoadingContact] = useState(false);
  const [savedMessage, setSavedMessage] = useState("");

  // ✅ إضافة مستخدم جديد
  const [showAddUser, setShowAddUser] = useState(false);
  const [creatingUser, setCreatingUser] = useState(false);
  const [createUserError, setCreateUserError] = useState("");
  const [createUserSuccess, setCreateUserSuccess] = useState("");
  const [newUser, setNewUser] = useState({
    name: "",
    email: "",
    phone: "",
    password: "",
    officeName: "",
    role: "محامي رئيسي",
  });

  /* ===========================================================
   ✅ Helpers
  =========================================================== */

  const normalize = (v) => String(v ?? "").trim().toLowerCase();

  const copyToClipboard = useCallback(async (text) => {
    try {
      await navigator.clipboard.writeText(String(text));
    } catch (e) {
      console.error("Copy failed:", e);
      alert("❌ فشل النسخ");
    }
  }, []);

  // ✅ نفس منطق الباك
  const normalizeEgyptPhone = useCallback((input) => {
    let p = String(input || "").replace(/\D/g, "");

    if (p.startsWith("0020")) p = p.slice(4);
    if (p.startsWith("20")) p = p.slice(2);

    if (p.length === 10 && p.startsWith("1")) p = "0" + p;

    return p;
  }, []);

  const isValidEmail = (email) =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(email || "").trim());

  const isValidEgyptPhone = (phone) =>
    /^01[0125][0-9]{8}$/.test(String(phone || "").trim());

  const getPhoneStatus = useCallback(
    (phone) => {
      const raw = String(phone ?? "").trim();
      if (!raw) return { type: "empty", label: "غير موجود" };

      const normalizedPhone = normalizeEgyptPhone(raw);
      const ok = isValidEgyptPhone(normalizedPhone);

      if (ok) return { type: "valid", label: "صحيح" };
      return { type: "invalid", label: "غير صحيح" };
    },
    [normalizeEgyptPhone]
  );

  const filteredUsers = useMemo(() => {
    const q = normalize(userSearch);
    if (!q) return users;

    return users.filter((u) => {
      const name = normalize(u?.name);
      const email = normalize(u?.email);
      const phone = normalize(u?.phone);
      const office = normalize(u?.officeName);
      const role = normalize(u?.role);

      return (
        name.includes(q) ||
        email.includes(q) ||
        phone.includes(q) ||
        office.includes(q) ||
        role.includes(q)
      );
    });
  }, [users, userSearch]);

  /* ===========================================================
   ✅ API Calls
  =========================================================== */

  const fetchPlans = async () => {
    try {
      const res = await api.get("/settings/plans");
      setPlans(res.data || []);
    } catch (err) {
      console.error("❌ فشل تحميل الخطط:", err);
    }
  };

  const fetchSettings = async () => {
    try {
      const res = await api.get("/settings/subscription-price");
      setContactInfo((prev) => ({
        ...prev,
        subscriptionPrice: res.data?.price ?? "",
        subscriptionDurationDays: res.data?.duration ?? "",
      }));
    } catch (err) {
      console.error("❌ فشل تحميل إعدادات الاشتراك:", err);
    }
  };

  const fetchAll = async () => {
    try {
      const [usersRes, refRes, notiRes, contactRes] = await Promise.all([
        api.get("/auth/users"),
        api.get("/auth/referrals"),
        api.get("/notifications"),
        api.get("/settings/contact-info").catch(() => ({ data: {} })),
      ]);

      setUsers(usersRes.data?.users || usersRes.data || []);
      setReferrals(refRes.data?.referrals || refRes.data || []);

      const allNotis = notiRes.data || [];
      const filtered = allNotis.filter((n) => !n.isSystem);
      setNotifications(filtered);

      setContactInfo((prev) => ({ ...prev, ...(contactRes.data || {}) }));
    } catch (err) {
      console.error("❌ خطأ في تحميل البيانات:", err);
    }
  };

  useEffect(() => {
    fetchPlans();
    fetchSettings();
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ===========================================================
   ✅ Actions
  =========================================================== */

  const deleteUser = async (id) => {
    if (!window.confirm("هل تريد حذف هذا المستخدم؟")) return;
    try {
      await api.delete(`/auth/users/${id}`);
      setUsers((prev) => prev.filter((u) => u._id !== id));
      alert("✅ تم حذف المستخدم بنجاح");
    } catch {
      alert("❌ فشل حذف المستخدم");
    }
  };

  const handleLogout = () => {
    localStorage.clear();
    window.location.href = "/";
  };

  const createUser = async () => {
    setCreateUserError("");
    setCreateUserSuccess("");

    const payload = {
      name: String(newUser.name || "").trim(),
      email: String(newUser.email || "").trim().toLowerCase(),
      phone: normalizeEgyptPhone(newUser.phone),
      password: String(newUser.password || "").trim(),
      officeName: String(newUser.officeName || "").trim() || null,
      role: newUser.role || "محامي رئيسي",
    };

    if (!payload.name || payload.name.length < 2) {
      return setCreateUserError("⚠️ اكتب اسم صحيح (حرفين على الأقل).");
    }

    if (!payload.email || !isValidEmail(payload.email)) {
      return setCreateUserError("⚠️ البريد الإلكتروني غير صحيح.");
    }

    if (!payload.phone || !isValidEgyptPhone(payload.phone)) {
      return setCreateUserError("⚠️ رقم الموبايل غير صحيح (مثال: 01012345678).");
    }

    if (!payload.password || payload.password.length < 6) {
      return setCreateUserError("⚠️ كلمة المرور لازم تكون 6 حروف/أرقام على الأقل.");
    }

    try {
      setCreatingUser(true);

      const res = await api.post("/auth/users", payload, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });

      setCreateUserSuccess("✅ تم إنشاء المستخدم بنجاح.");
      setNewUser({
        name: "",
        email: "",
        phone: "",
        password: "",
        officeName: "",
        role: "محامي رئيسي",
      });

      const created = res?.data?.user || res?.data;
      if (created && created._id) {
        setUsers((prev) => [created, ...prev]);
      } else {
        fetchAll();
      }

      setTimeout(() => {
        setShowAddUser(false);
        setCreateUserSuccess("");
      }, 800);
    } catch (err) {
      const status = err?.response?.status;
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        "فشل إنشاء المستخدم";

      if (status === 404) {
        setCreateUserError("❌ Endpoint غير موجود: تأكد أن لديك POST /auth/users في الباك اند.");
      } else {
        setCreateUserError(`❌ ${msg}`);
      }
    } finally {
      setCreatingUser(false);
    }
  };

  // ✅ تحديث بيانات الأدمن باحترافية
  const handleUpdateAdmin = async (e) => {
    e.preventDefault();

    const payload = {};
    const normalizedPhone = normalizeEgyptPhone(newPhone);

    if (!newPhone.trim() && !newPassword.trim()) {
      return alert("⚠️ يرجى إدخال رقم الموبايل الجديد أو كلمة المرور الجديدة أولاً.");
    }

    if (newPhone.trim()) {
      if (!isValidEgyptPhone(normalizedPhone)) {
        return alert("⚠️ رقم الموبايل غير صالح (مثال صحيح: 01012345678).");
      }
      payload.newPhone = normalizedPhone;
    }

    if (newPassword.trim()) {
      if (newPassword.trim().length < 6) {
        return alert("⚠️ كلمة المرور لازم تكون 6 أحرف/أرقام على الأقل.");
      }
      payload.newPassword = newPassword.trim();
    }

    try {
      setUpdatingAdmin(true);

      const res = await api.put("/auth/update-admin", payload, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });

      alert(res?.data?.message || "✅ تم تحديث بيانات الأدمن بنجاح.");

      setNewPhone("");
      setNewPassword("");
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        "❌ فشل تحديث بيانات الأدمن. حاول مجددًا لاحقًا.";

      alert(msg);
      console.error("❌ handleUpdateAdmin:", err);
    } finally {
      setUpdatingAdmin(false);
    }
  };

  /* ===========================================================
   ✅ UI Helpers
  =========================================================== */

  const sectionButton = (icon, label, section) => (
    <button
      type="button"
      onClick={() => setActiveSection(section)}
      className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 ring-1 ring-transparent focus:outline-none focus:ring-2 focus:ring-blue-300 ${
        activeSection === section
          ? "bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-md"
          : "hover:bg-gray-50 text-gray-700 hover:shadow-sm"
      }`}
    >
      <span className="text-base">{icon}</span>
      <span className="text-sm md:text-[15px] font-semibold tracking-wide">{label}</span>
    </button>
  );

  const ToggleArrow = () => (
    <button
      type="button"
      onClick={() => setSidebarVisible((v) => !v)}
      className="fixed top-4 left-4 z-50 flex items-center justify-center h-10 w-10 rounded-xl bg-white shadow-md border border-gray-200 hover:bg-gray-50 active:scale-[.98] transition-all duration-200"
      title={sidebarVisible ? "إخفاء القائمة" : "إظهار القائمة"}
      aria-label={sidebarVisible ? "إخفاء القائمة" : "إظهار القائمة"}
    >
      {sidebarVisible ? <FaChevronLeft /> : <FaChevronRight />}
    </button>
  );

  return (
    <div
      dir="rtl"
      className="flex h-screen overflow-hidden bg-gradient-to-br from-slate-50 via-white to-slate-100 text-slate-900"
    >
      <ToggleArrow />

      <AddUserModal
        show={showAddUser}
        close={() => setShowAddUser(false)}
        creatingUser={creatingUser}
        newUser={newUser}
        setNewUser={setNewUser}
        createUser={createUser}
        error={createUserError}
        success={createUserSuccess}
        phoneOk={isValidEgyptPhone(normalizeEgyptPhone(newUser.phone))}
        normalizedForHint={normalizeEgyptPhone(newUser.phone)}
      />

      {sidebarVisible && (
        <div
          className="fixed inset-0 bg-black/35 md:hidden z-30 animate-fadeIn"
          onClick={() => setSidebarVisible(false)}
        />
      )}

      <aside
        aria-hidden={!sidebarVisible}
        className={[
          "fixed md:relative z-40 h-full",
          sidebarVisible
            ? "w-72 md:w-72 translate-x-0 pointer-events-auto"
            : "w-0 md:w-0 -translate-x-full md:-translate-x-full pointer-events-none",
          "bg-white/95 backdrop-blur border-e border-slate-200/80 shadow-xl md:shadow-none md:border-transparent md:bg-white",
          "transition-all duration-700 ease-in-out",
        ].join(" ")}
        style={{ willChange: "transform,width" }}
      >
        <div className="flex items-center justify-between p-4 border-b bg-gradient-to-r from-blue-50 to-indigo-50">
          <h2 className="text-lg font-extrabold text-blue-700 tracking-wide">Avocato Admin</h2>

          <button
            type="button"
            className="md:hidden p-2 hover:bg-slate-100 rounded-lg transition"
            onClick={() => setSidebarVisible(false)}
            aria-label="إغلاق القائمة"
            title="إغلاق القائمة"
          >
            <FaBars />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-3 space-y-2 mt-2 custom-scroll">
          {sectionButton(<FaChartPie />, "لوحة التحكم", "dashboard")}
          {sectionButton(<FaUsers />, "المستخدمين", "users")}
          {sectionButton(<FaLink />, "الإحالات", "referrals")}
          {sectionButton(<FaBell />, "الإشعارات", "notifications")}
          {sectionButton(<FaPhoneAlt />, "بيانات التواصل", "contact")}
          {sectionButton(<FaCog />, "الإعدادات", "settings")}
        </div>

        <div className="p-4">
          <button
            type="button"
            onClick={handleLogout}
            className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-rose-500 to-rose-600 hover:from-rose-600 hover:to-rose-700 text-white py-2.5 rounded-xl font-bold shadow-md hover:shadow-lg transition-all"
          >
            <FaSignOutAlt className="text-sm" /> خروج
          </button>
        </div>
      </aside>

      <main className="flex-1 flex flex-col overflow-y-auto">
        <div className="md:hidden flex items-center justify-center px-4 py-3 bg-white/90 backdrop-blur border-b sticky top-0 z-30">
          <h1 className="text-lg font-extrabold text-blue-700">Avocato Admin</h1>
        </div>

        <div className="p-4 md:p-8 max-w-[1200px] mx-auto w-full">
          <h1 className="text-2xl md:text-3xl font-extrabold mb-6 text-slate-900 flex items-center gap-3 tracking-tight">
            👑 لوحة تحكم المدير
          </h1>

          {activeSection === "dashboard" && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 animate-stagger-parent">
              {[
                {
                  title: "👥 المستخدمين",
                  value: users.length,
                  ring: "ring-blue-200",
                  bg: "bg-gradient-to-br from-blue-50 to-white",
                  text: "text-blue-900",
                },
                {
                  title: "🔗 الإحالات",
                  value: referrals.length,
                  ring: "ring-amber-200",
                  bg: "bg-gradient-to-br from-amber-50 to-white",
                  text: "text-amber-900",
                },
                {
                  title: "🔔 الإشعارات",
                  value: notifications.length,
                  ring: "ring-violet-200",
                  bg: "bg-gradient-to-br from-violet-50 to-white",
                  text: "text-violet-900",
                },
              ].map((card, i) => (
                <div
                  key={i}
                  className={`${card.bg} ${card.text} border border-slate-200/70 ring-1 ${card.ring} rounded-2xl p-5 text-center shadow-sm hover:shadow-lg hover:-translate-y-0.5 transition-all animate-card`}
                  style={{ animationDelay: `${i * 80}ms` }}
                >
                  <h3 className="text-base font-bold">{card.title}</h3>
                  <p className="text-3xl md:text-4xl font-black mt-2">{card.value}</p>
                </div>
              ))}

              {notifications.length > 0 && (
                <div
                  className="bg-white/90 backdrop-blur border border-slate-200/70 rounded-2xl p-5 shadow-sm hover:shadow-md transition animate-card"
                  style={{ animationDelay: "280ms" }}
                >
                  <h3 className="text-[15px] font-bold text-blue-700">📢 أحدث إشعار</h3>
                  <p className="mt-2 text-sm text-slate-700 line-clamp-2">{notifications[0].title}</p>
                  <p className="text-slate-500 text-xs mt-1">
                    {new Date(notifications[0].createdAt).toLocaleString("ar-EG")}
                  </p>
                </div>
              )}
            </div>
          )}

          {activeSection === "users" && (
            <div className="bg-white/95 backdrop-blur rounded-2xl shadow-md p-6 mt-6 border border-slate-200/80 animate-section">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4 border-b pb-3">
                <div>
                  <h2 className="text-xl md:text-2xl font-extrabold text-slate-900">
                    👥 جميع المستخدمين ({filteredUsers.length})
                  </h2>
                  <p className="text-xs md:text-sm text-slate-500 mt-1">
                    يمكنك إضافة مستخدم جديد من زر (إضافة مستخدم).
                  </p>
                </div>

                <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto">
                  <input
                    type="text"
                    value={userSearch}
                    onChange={(e) => setUserSearch(e.target.value)}
                    placeholder="🔎 ابحث بالاسم / الإيميل / رقم الموبايل / المكتب..."
                    className="w-full md:w-[420px] border border-slate-200 p-3 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400"
                  />

                  <button
                    type="button"
                    onClick={() => {
                      setCreateUserError("");
                      setCreateUserSuccess("");
                      setShowAddUser(true);
                    }}
                    className="w-full sm:w-auto px-4 py-3 rounded-xl font-extrabold text-white bg-blue-600 hover:bg-blue-700 transition shadow-sm flex items-center justify-center gap-2"
                  >
                    <FaPlus /> إضافة مستخدم
                  </button>
                </div>
              </div>

              <div className="overflow-x-auto rounded-2xl border border-slate-200/80 hidden md:block">
                <table className="w-full text-[15px]">
                  <thead className="bg-slate-50">
                    <tr className="text-slate-700">
                      <th className="p-3 text-start">الاسم</th>
                      <th className="p-3 text-start">البريد الإلكتروني</th>
                      <th className="p-3 text-start">الدور</th>
                      <th className="p-3 text-start">الهاتف</th>
                      <th className="p-3 text-start">حالة الهاتف</th>
                      <th className="p-3 text-start">اسم المكتب</th>
                      <th className="p-3 text-center">إجراء</th>
                    </tr>
                  </thead>

                  <tbody className="divide-y divide-slate-100">
                    {filteredUsers.map((u) => {
                      const phoneStatus = getPhoneStatus(u.phone);
                      const badgeClass =
                        phoneStatus.type === "valid"
                          ? "bg-green-50 text-green-700 border-green-200"
                          : phoneStatus.type === "invalid"
                          ? "bg-rose-50 text-rose-700 border-rose-200"
                          : "bg-slate-50 text-slate-600 border-slate-200";

                      return (
                        <tr key={u._id} className="hover:bg-slate-50/80 transition-colors">
                          <td className="p-3 font-semibold text-slate-800">{u.name || "—"}</td>
                          <td className="p-3">{u.email || "—"}</td>
                          <td className="p-3">{u.role || "—"}</td>

                          <td className="p-3">
                            <div className="flex items-center gap-2">
                              <span>{u.phone || "—"}</span>

                              {u.phone && (
                                <a
                                  href={`https://wa.me/2${u.phone.replace(/^0/, "")}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-green-600 hover:text-green-700"
                                >
                                  <FaWhatsapp />
                                </a>
                              )}

                              {u.phone && (
                                <button
                                  type="button"
                                  onClick={() => copyToClipboard(u.phone)}
                                  className="text-xs px-2 py-1 rounded-lg bg-slate-100 hover:bg-slate-200 transition font-semibold"
                                >
                                  نسخ
                                </button>
                              )}
                            </div>
                          </td>

                          <td className="p-3">
                            <span
                              className={`inline-flex px-2 py-1 rounded-lg text-xs font-bold border ${badgeClass}`}
                            >
                              {phoneStatus.label}
                            </span>
                          </td>

                          <td className="p-3">{u.officeName || "—"}</td>

                          <td className="p-3">
                            <div className="flex items-center justify-center gap-2">
                              <button
                                type="button"
                                onClick={async () => {
                                  const amt = prompt("عدد الرسائل لإضافتها:");
                                  if (!amt) return;
                                  await api.post("/auth/add-messages", {
                                    userId: u._id,
                                    amount: Number(amt),
                                  });
                                  fetchAll();
                                }}
                                className="px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg font-semibold"
                              >
                                ➕ رسائل
                              </button>

                              <button
                                type="button"
                                onClick={() => deleteUser(u._id)}
                                className="px-4 py-1.5 bg-rose-500 hover:bg-rose-600 text-white rounded-lg font-semibold transition shadow-sm"
                              >
                                حذف
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:hidden animate-stagger-parent mt-4">
                {filteredUsers.map((u, i) => {
                  const phoneStatus = getPhoneStatus(u.phone);
                  const badgeClass =
                    phoneStatus.type === "valid"
                      ? "bg-green-50 text-green-700 border-green-200"
                      : phoneStatus.type === "invalid"
                      ? "bg-rose-50 text-rose-700 border-rose-200"
                      : "bg-slate-50 text-slate-600 border-slate-200";

                  return (
                    <div
                      key={u._id}
                      className="border border-slate-200 rounded-2xl p-4 bg-white shadow-sm hover:shadow-md transition animate-card"
                      style={{ animationDelay: `${i * 70}ms` }}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="font-extrabold text-slate-900">{u.name || "—"}</h3>
                        <span className="text-xs px-2 py-1 rounded-lg bg-slate-100 text-slate-700">
                          {u.role || "—"}
                        </span>
                      </div>

                      <div className="text-[13px] space-y-1.5 text-slate-700">
                        <p>
                          <span className="font-semibold">📧 البريد:</span> {u.email || "—"}
                        </p>

                        <div className="flex items-center justify-between gap-2">
                          <p className="truncate">
                            <span className="font-semibold">📞 الهاتف:</span> {u.phone || "—"}
                          </p>

                          {u.phone && (
                            <button
                              type="button"
                              onClick={() => copyToClipboard(u.phone)}
                              className="text-xs px-2 py-1 rounded-lg bg-slate-100 hover:bg-slate-200 transition font-semibold"
                            >
                              نسخ
                            </button>
                          )}
                        </div>

                        {u.phone && (
                          <a
                            href={`https://wa.me/2${u.phone.replace(/^0/, "")}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-green-600 font-bold"
                          >
                            <FaWhatsapp /> واتساب
                          </a>
                        )}

                        <p>
                          <span className="font-semibold">✅ حالة الهاتف:</span>
                          <span
                            className={`inline-flex px-2 py-0.5 rounded-lg text-xs font-bold border ml-2 ${badgeClass}`}
                          >
                            {phoneStatus.label}
                          </span>
                        </p>

                        <p>
                          <span className="font-semibold">🏢 المكتب:</span> {u.officeName || "—"}
                        </p>
                      </div>

                      <div className="mt-3 space-y-2">
                        <button
                          type="button"
                          onClick={async () => {
                            const amt = prompt("عدد الرسائل لإضافتها:");
                            if (!amt) return;
                            await api.post("/auth/add-messages", {
                              userId: u._id,
                              amount: Number(amt),
                            });
                            fetchAll();
                          }}
                          className="w-full py-2 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white font-bold transition"
                        >
                          ➕ إضافة رسائل AI
                        </button>

                        <button
                          type="button"
                          onClick={() => deleteUser(u._id)}
                          className="w-full py-2 rounded-xl bg-rose-500 hover:bg-rose-600 text-white font-bold transition"
                        >
                          حذف
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {activeSection === "notifications" && (
            <div className="bg-white/95 backdrop-blur rounded-2xl shadow-md p-6 mt-6 border border-slate-200/80 animate-section">
              <h2 className="text-xl md:text-2xl font-extrabold mb-6 text-slate-900 border-b pb-3 flex items-center gap-2">
                <FaBell className="text-blue-500" /> إدارة الإشعارات
              </h2>

              <form
                onSubmit={async (e) => {
                  e.preventDefault();
                  if (!title.trim() || !message.trim()) {
                    return alert("⚠️ أدخل عنوان الإشعار ومحتواه");
                  }

                  try {
                    setSending(true);
                    const body = {
                      title: title.trim(),
                      message: message.trim(),
                      isGlobal,
                    };

                    if (!isGlobal) body.recipients = recipients;

                    await api.post("/notifications", body, {
                      headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
                    });

                    alert("✅ تم إرسال الإشعار بنجاح");
                    setTitle("");
                    setMessage("");
                    setRecipients([]);
                    setIsGlobal(true);
                    fetchAll();
                  } catch (err) {
                    console.error(err);
                    alert("❌ فشل إرسال الإشعار");
                  } finally {
                    setSending(false);
                  }
                }}
                className="space-y-4 mb-8 bg-gradient-to-br from-slate-50 to-white p-5 rounded-2xl border border-slate-200/80 shadow-sm"
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <input
                    type="text"
                    placeholder="عنوان الإشعار"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    className="border border-slate-200 p-3 rounded-xl w-full focus:outline-none focus:ring-2 focus:ring-blue-400"
                  />

                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="isGlobal"
                      checked={isGlobal}
                      onChange={(e) => setIsGlobal(e.target.checked)}
                      className="size-4 accent-blue-600"
                    />
                    <label htmlFor="isGlobal" className="font-medium">
                      إرسال لجميع المستخدمين
                    </label>
                  </div>
                </div>

                <textarea
                  placeholder="نص الإشعار..."
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  rows="3"
                  className="border border-slate-200 p-3 rounded-xl w-full focus:outline-none focus:ring-2 focus:ring-blue-400"
                />

                {!isGlobal && (
                  <div>
                    <label className="block font-semibold mb-1">اختر المستلمين:</label>
                    <select
                      multiple
                      value={recipients}
                      onChange={(e) =>
                        setRecipients([...e.target.selectedOptions].map((o) => o.value))
                      }
                      className="w-full border border-slate-200 p-2 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400"
                    >
                      {users.map((u) => (
                        <option key={u._id} value={u._id}>
                          {u.name} ({u.email})
                        </option>
                      ))}
                    </select>
                    <small className="text-slate-500">
                      استخدم Ctrl/Command لتحديد أكثر من مستخدم
                    </small>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={sending}
                  className={`w-full font-bold py-2.5 rounded-xl transition-all shadow-sm ${
                    sending ? "bg-slate-400 text-white" : "bg-blue-600 hover:bg-blue-700 text-white"
                  }`}
                >
                  {sending ? "⏳ جاري الإرسال..." : "🚀 إرسال الإشعار"}
                </button>
              </form>

              {notifications.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 animate-stagger-parent">
                  {notifications.map((n, i) => (
                    <div
                      key={n._id || i}
                      className="relative bg-gradient-to-br from-slate-50 to-white border border-slate-200/80 rounded-2xl shadow-sm hover:shadow-lg p-5 transition-all group animate-card"
                      style={{ animationDelay: `${i * 70}ms` }}
                    >
                      <h3 className="text-base font-bold text-blue-700 mb-2">{n.title}</h3>
                      <p className="text-slate-700 text-sm mb-2 line-clamp-2">{n.message}</p>
                      <p className="text-slate-500 text-xs mb-3">
                        🕒 {new Date(n.createdAt).toLocaleString("ar-EG")}
                      </p>

                      <div className="flex justify-between items-center">
                        <button
                          type="button"
                          onClick={() => setActiveNotification(n)}
                          className="text-sm font-semibold text-blue-600 hover:underline"
                        >
                          عرض التفاصيل
                        </button>

                        <button
                          type="button"
                          onClick={async () => {
                            if (!window.confirm("هل تريد حذف هذا الإشعار؟")) return;
                            try {
                              await api.delete(`/notifications/${n._id}`, {
                                headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
                              });
                              alert("✅ تم حذف الإشعار بنجاح");
                              fetchAll();
                            } catch (err) {
                              alert("❌ فشل حذف الإشعار");
                              console.error(err);
                            }
                          }}
                          className="text-rose-600 text-sm hover:text-rose-700 font-semibold"
                        >
                          حذف
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-center text-slate-500 italic mt-6">
                  📭 لا توجد إشعارات تمت إضافتها بعد.
                </p>
              )}

              {activeNotification && (
                <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4 animate-fadeIn">
                  <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg p-6 relative border border-slate-200 animate-popIn">
                    <button
                      type="button"
                      onClick={() => setActiveNotification(null)}
                      className="absolute top-3 left-4 text-slate-500 hover:text-slate-700"
                      aria-label="إغلاق"
                    >
                      ✕
                    </button>

                    <h3 className="text-2xl font-extrabold text-blue-700 mb-2">
                      {activeNotification.title}
                    </h3>
                    <p className="text-slate-700 mb-4">{activeNotification.message}</p>
                    <p className="text-slate-500 text-sm mb-4">
                      🕒 {new Date(activeNotification.createdAt).toLocaleString("ar-EG")}
                    </p>

                    <hr className="my-4" />
                    <h4 className="font-bold text-slate-900 mb-2">💬 الردود:</h4>

                    {activeNotification.replies?.length ? (
                      <div className="space-y-2 max-h-48 overflow-y-auto custom-scroll">
                        {activeNotification.replies.map((r, i) => (
                          <div
                            key={i}
                            className="bg-slate-50 border border-slate-200 p-2 rounded-lg text-sm text-slate-700"
                          >
                            <strong>{r.user?.name || "مستخدم"}:</strong> {r.message}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-slate-400 text-sm">لا توجد ردود بعد</p>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {activeSection === "referrals" && (
            <div className="bg-white/95 backdrop-blur rounded-2xl shadow-md p-6 mt-6 border border-slate-200/80 animate-section">
              <h2 className="text-xl md:text-2xl font-extrabold mb-4 text-slate-900 border-b pb-3">
                🔗 نظام الإحالات ({referrals.length})
              </h2>

              <div className="overflow-x-auto rounded-2xl border border-slate-200/80 hidden md:block">
                <table className="w-full text-[15px]">
                  <thead className="bg-slate-50">
                    <tr className="text-slate-700">
                      <th className="p-3 text-start">الاسم</th>
                      <th className="p-3 text-start">البريد</th>
                      <th className="p-3 text-center">عدد الإحالات</th>
                      <th className="p-3 text-start">الأشخاص المدعوّين</th>
                      <th className="p-3 text-center">🎁 المكافأة</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {referrals.length > 0 ? (
                      referrals.map((r) => (
                        <tr key={r._id} className="hover:bg-slate-50/80 transition-colors">
                          <td className="p-3 font-semibold text-slate-800">{r.name || "—"}</td>
                          <td className="p-3">{r.email || "—"}</td>
                          <td className="p-3 text-blue-700 font-extrabold text-center">
                            {r.referrals?.length || 0}
                          </td>
                          <td className="p-3 text-slate-700">
                            {r.referrals?.length
                              ? r.referrals.map((u) => u.name || u.email).join(", ")
                              : "—"}
                          </td>
                          <td className="p-3 text-center">
                            {r.referrals?.length >= 5 ? (
                              <span className="inline-flex items-center justify-center gap-1 text-green-700 font-bold px-2 py-1 rounded-lg bg-green-50 border border-green-200">
                                <FaGift /> خصم 10%
                              </span>
                            ) : (
                              <span className="text-slate-400">لا يوجد مكافأة</span>
                            )}
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="5" className="p-5 text-slate-500 text-center italic">
                          لا توجد إحالات حتى الآن.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:hidden animate-stagger-parent mt-4">
                {referrals.length > 0 ? (
                  referrals.map((r, i) => (
                    <div
                      key={r._id}
                      className="border border-slate-200 rounded-2xl p-4 bg-white shadow-sm hover:shadow-md transition animate-card"
                      style={{ animationDelay: `${i * 70}ms` }}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="font-extrabold text-slate-900">{r.name || "—"}</h3>
                        <span className="text-xs px-2 py-1 rounded-lg bg-blue-50 text-blue-700">
                          {r.referrals?.length || 0} إحالة
                        </span>
                      </div>

                      <p className="text-[13px] text-slate-700 mb-2">
                        <span className="font-semibold">📧 البريد:</span> {r.email || "—"}
                      </p>

                      <p className="text-[13px] text-slate-700">
                        <span className="font-semibold">👥 المدعوّين:</span>{" "}
                        {r.referrals?.length
                          ? r.referrals.map((u) => u.name || u.email).join(", ")
                          : "—"}
                      </p>

                      <div className="mt-3">
                        {r.referrals?.length >= 5 ? (
                          <span className="inline-flex items-center gap-1 text-green-700 font-bold px-2 py-1 rounded-lg bg-green-50 border border-green-200">
                            <FaGift /> خصم 10%
                          </span>
                        ) : (
                          <span className="text-slate-400">لا يوجد مكافأة</span>
                        )}
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-slate-500 text-center col-span-full">لا توجد إحالات حتى الآن.</p>
                )}
              </div>
            </div>
          )}

          {activeSection === "contact" && (
            <div className="bg-white/95 backdrop-blur rounded-2xl shadow-md p-6 mt-6 border border-slate-200/80 animate-section">
              <h2 className="text-xl md:text-2xl font-extrabold mb-6 text-slate-900 border-b pb-3 flex items-center gap-2">
                <FaPhoneAlt className="text-blue-500" /> بيانات التواصل
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="animate-card">
                  <label className="font-semibold text-slate-700 flex items-center gap-2 mb-1">
                    <FaWhatsapp className="text-green-500" /> رقم واتساب
                  </label>
                  <input
                    type="text"
                    value={contactInfo.whatsapp || ""}
                    onChange={(e) => setContactInfo((p) => ({ ...p, whatsapp: e.target.value }))}
                    placeholder="مثال: 201234567890"
                    className="border border-slate-200 p-3 rounded-xl w-full focus:outline-none focus:ring-2 focus:ring-blue-400"
                  />
                </div>

                <div className="animate-card" style={{ animationDelay: "80ms" }}>
                  <label className="font-semibold text-slate-700 flex items-center gap-2 mb-1">
                    <FaFacebook className="text-blue-600" /> رابط فيسبوك
                  </label>
                  <input
                    type="text"
                    value={contactInfo.facebook || ""}
                    onChange={(e) => setContactInfo((p) => ({ ...p, facebook: e.target.value }))}
                    placeholder="https://facebook.com/yourpage"
                    className="border border-slate-200 p-3 rounded-xl w-full focus:outline-none focus:ring-2 focus:ring-blue-400"
                  />
                </div>

                <div className="animate-card" style={{ animationDelay: "160ms" }}>
                  <label className="font-semibold text-slate-700 flex items-center gap-2 mb-1">
                    <FaEnvelope className="text-amber-500" /> البريد الإلكتروني
                  </label>
                  <input
                    type="email"
                    value={contactInfo.email || ""}
                    onChange={(e) => setContactInfo((p) => ({ ...p, email: e.target.value }))}
                    placeholder="example@email.com"
                    className="border border-slate-200 p-3 rounded-xl w-full focus:outline-none focus:ring-2 focus:ring-blue-400"
                  />
                </div>
              </div>

              <div className="mt-6 flex items-center gap-4">
                <button
                  type="button"
                  onClick={async () => {
                    if (!contactInfo.whatsapp && !contactInfo.facebook && !contactInfo.email) {
                      return alert("⚠️ أدخل بيانات واحدة على الأقل");
                    }
                    try {
                      setLoadingContact(true);
                      await api.put("/settings/contact-info", contactInfo);
                      setSavedMessage("✅ تم حفظ بيانات التواصل بنجاح!");
                      setTimeout(() => setSavedMessage(""), 3000);
                    } catch {
                      alert("❌ حدث خطأ أثناء الحفظ");
                    } finally {
                      setLoadingContact(false);
                    }
                  }}
                  disabled={loadingContact}
                  className={`px-6 py-2.5 rounded-xl font-bold transition shadow-sm ${
                    loadingContact ? "bg-slate-400 text-white" : "bg-blue-600 hover:bg-blue-700 text-white"
                  }`}
                >
                  {loadingContact ? "⏳ جاري الحفظ..." : "💾 حفظ البيانات"}
                </button>

                {savedMessage && <span className="text-green-600 font-semibold">{savedMessage}</span>}
              </div>
            </div>
          )}

          {activeSection === "settings" && (
            <div className="bg-gradient-to-b from-slate-50 to-white rounded-2xl shadow-xl p-8 mt-6 border border-slate-200/80 mb-10 transition-all duration-300 animate-section">
              <h2 className="text-3xl font-extrabold mb-8 text-slate-900 flex items-center gap-3 border-b pb-3">
                ⚙️ إعدادات النظام والإدارة
              </h2>

              {/* ✅ تحديث بيانات الأدمن - تم إصلاحها بشكل احترافي */}
              <div className="mb-12">
                <h3 className="text-2xl font-semibold text-blue-700 mb-4 flex items-center gap-2">
                  👤 تحديث بيانات دخول الأدمن
                </h3>

                <form
                  onSubmit={handleUpdateAdmin}
                  className="space-y-4 bg-blue-50/60 border border-blue-100 p-6 rounded-2xl shadow-inner"
                >
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-bold text-slate-700 mb-2">
                        📱 رقم الموبايل الجديد
                      </label>
                      <input
                        type="text"
                        placeholder="مثال: 01012345678"
                        value={newPhone}
                        onChange={(e) => setNewPhone(e.target.value)}
                        className="border border-slate-200 p-3 rounded-xl w-full focus:outline-none focus:ring-2 focus:ring-blue-400"
                      />
                      {newPhone.trim() && (
                        <small
                          className={`mt-2 block font-medium ${
                            isValidEgyptPhone(normalizeEgyptPhone(newPhone))
                              ? "text-green-600"
                              : "text-rose-600"
                          }`}
                        >
                          {isValidEgyptPhone(normalizeEgyptPhone(newPhone))
                            ? `✅ سيتم الحفظ بصيغة: ${normalizeEgyptPhone(newPhone)}`
                            : "⚠️ رقم الموبايل غير صالح"}
                        </small>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-bold text-slate-700 mb-2">
                        🔑 كلمة المرور الجديدة
                      </label>
                      <input
                        type="password"
                        placeholder="6 أحرف/أرقام على الأقل"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        className="border border-slate-200 p-3 rounded-xl w-full focus:outline-none focus:ring-2 focus:ring-blue-400"
                      />
                      {newPassword.trim() && (
                        <small
                          className={`mt-2 block font-medium ${
                            newPassword.trim().length >= 6 ? "text-green-600" : "text-rose-600"
                          }`}
                        >
                          {newPassword.trim().length >= 6
                            ? "✅ كلمة المرور صالحة"
                            : "⚠️ كلمة المرور قصيرة جدًا"}
                        </small>
                      )}
                    </div>
                  </div>

                  <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
هنا يتم تعديل بيانات دخول الادمن                  </div>

                  <button
                    type="submit"
                    disabled={updatingAdmin}
                    className={`w-full mt-2 px-6 py-3 rounded-xl font-bold transition-all shadow-sm ${
                      updatingAdmin
                        ? "bg-slate-400 text-white cursor-not-allowed"
                        : "bg-blue-600 hover:bg-blue-700 text-white"
                    }`}
                  >
                    {updatingAdmin ? "⏳ جاري حفظ البيانات..." : "💾 حفظ بيانات الدخول"}
                  </button>
                </form>
              </div>

              {/* 💳 الإعدادات الافتراضية للاشتراك */}
              <div className="border-t pt-8 mb-12">
                <h3 className="text-2xl font-semibold text-green-700 mb-6 flex items-center gap-2">
                  💳 الإعدادات الافتراضية للاشتراك
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div className="flex flex-col">
                    <label className="font-semibold text-slate-700 mb-2">
                      💰 سعر الاشتراك الافتراضي (بالجنيه)
                    </label>
                    <input
                      type="number"
                      min="0"
                      value={contactInfo.subscriptionPrice || ""}
                      onChange={(e) =>
                        setContactInfo((p) => ({
                          ...p,
                          subscriptionPrice: Number(e.target.value),
                        }))
                      }
                      placeholder="مثلاً: 150 أو 0 للخطة المجانية"
                      className="border border-slate-200 p-3 rounded-xl w-full focus:outline-none focus:ring-2 focus:ring-green-400"
                    />
                    <small className="text-slate-500 mt-1">
                      إذا جعلت السعر <b>0</b> ستكون الخطة مجانية ✨
                    </small>
                  </div>

                  <div className="flex flex-col">
                    <label className="font-semibold text-slate-700 mb-2">⏳ مدة الاشتراك (بالأيام)</label>
                    <input
                      type="number"
                      min="1"
                      value={contactInfo.subscriptionDurationDays || ""}
                      onChange={(e) =>
                        setContactInfo((p) => ({
                          ...p,
                          subscriptionDurationDays: Number(e.target.value),
                        }))
                      }
                      placeholder="مثلاً: 30"
                      className="border border-slate-200 p-3 rounded-xl w-full focus:outline-none focus:ring-2 focus:ring-green-400"
                    />
                  </div>
                </div>

                <div className="mt-8 flex justify-end">
                  <button
                    type="button"
                    onClick={async () => {
                      if (
                        contactInfo.subscriptionPrice === undefined ||
                        !contactInfo.subscriptionDurationDays
                      ) {
                        return alert("⚠️ أدخل السعر والمدة أولاً");
                      }

                      try {
                        await api.put("/settings/subscription-price", {
                          price: contactInfo.subscriptionPrice,
                          duration: contactInfo.subscriptionDurationDays,
                        });
                        alert("✅ تم حفظ الإعدادات الافتراضية بنجاح!");
                      } catch (err) {
                        console.error(err);
                        alert("❌ حدث خطأ أثناء حفظ إعدادات الاشتراك.");
                      }
                    }}
                    className="bg-green-600 hover:bg-green-700 text-white px-8 py-3 rounded-xl font-bold transition-all shadow-sm"
                  >
                    💾 حفظ الإعدادات الافتراضية
                  </button>
                </div>
              </div>

              {/* 🧾 إدارة خطط الاشتراك */}
              <div className="border-t pt-8">
                <h3 className="text-2xl font-semibold text-purple-700 mb-6 flex items-center gap-2">
                  🧾 إدارة خطط الاشتراك
                </h3>

                <div className="grid md:grid-cols-4 gap-4 mb-6 bg-purple-50/60 border border-purple-100 p-6 rounded-2xl shadow-inner">
                  <input
                    type="text"
                    placeholder="📦 اسم الخطة"
                    value={newPlan.name}
                    onChange={(e) => setNewPlan((p) => ({ ...p, name: e.target.value }))}
                    className="border border-slate-200 p-3 rounded-xl w-full focus:outline-none focus:ring-2 focus:ring-purple-400"
                  />
                  <input
                    type="number"
                    placeholder="💰 السعر بالجنيه"
                    value={newPlan.price}
                    min="0"
                    onChange={(e) => setNewPlan((p) => ({ ...p, price: Number(e.target.value) }))}
                    className="border border-slate-200 p-3 rounded-xl w-full focus:outline-none focus:ring-2 focus:ring-purple-400"
                  />
                  <input
                    type="number"
                    placeholder="⏳ المدة (يوم)"
                    value={newPlan.durationDays}
                    min="1"
                    onChange={(e) =>
                      setNewPlan((p) => ({ ...p, durationDays: Number(e.target.value) }))
                    }
                    className="border border-slate-200 p-3 rounded-xl w-full focus:outline-none focus:ring-2 focus:ring-purple-400"
                  />
                  <input
                    type="number"
                    placeholder="🎁 أيام مجانية (اختياري)"
                    value={newPlan.trialDays}
                    onChange={(e) =>
                      setNewPlan((p) => ({ ...p, trialDays: Number(e.target.value) }))
                    }
                    className="border border-slate-200 p-3 rounded-xl w-full focus:outline-none focus:ring-2 focus:ring-purple-400"
                  />
                </div>

                <div className="flex justify-end mb-8">
                  <button
                    type="button"
                    onClick={async () => {
                      if (!newPlan.name || newPlan.price === "" || !newPlan.durationDays) {
                        return alert("⚠️ أدخل جميع بيانات الخطة المطلوبة.");
                      }

                      try {
                        await api.post("/settings/plans", newPlan);
                        alert(
                          newPlan.price === 0
                            ? "🎁 تم إنشاء الخطة المجانية بنجاح!"
                            : "✅ تم إنشاء الخطة بنجاح!"
                        );
                        setNewPlan({ name: "", price: "", durationDays: "", trialDays: "" });
                        fetchPlans();
                      } catch (err) {
                        console.error("❌ فشل إنشاء الخطة:", err);
                        alert("❌ حدث خطأ أثناء إضافة الخطة.");
                      }
                    }}
                    className="bg-purple-600 hover:bg-purple-700 text-white px-8 py-3 rounded-xl font-bold transition-all shadow-sm"
                  >
                    ➕ إضافة خطة جديدة
                  </button>
                </div>

                {plans.length > 0 ? (
                  <>
                    <div className="overflow-x-auto rounded-2xl border border-slate-200/80 hidden md:block">
                      <table className="min-w-full text-center">
                        <thead className="bg-slate-50 text-slate-800 font-semibold">
                          <tr>
                            <th className="py-3 px-4">📦 الاسم</th>
                            <th className="py-3 px-4">💰 السعر</th>
                            <th className="py-3 px-4">⏳ المدة (يوم)</th>
                            <th className="py-3 px-4">🎁 الأيام المجانية</th>
                            <th className="py-3 px-4">⚙️ الإجراءات</th>
                          </tr>
                        </thead>

                        <tbody className="divide-y divide-slate-100">
                          {plans.map((p) => (
                            <tr
                              key={p._id}
                              className={`transition-colors ${
                                p.price === 0 ? "bg-green-50" : "hover:bg-slate-50/60"
                              }`}
                            >
                              <td className="py-3 px-4 font-semibold">
                                {p.price === 0 ? "🎁 " : ""}
                                {p.name}
                              </td>

                              <td
                                className={`py-3 px-4 font-bold ${
                                  p.price === 0 ? "text-green-700" : "text-blue-700"
                                }`}
                              >
                                {p.price === 0 ? "مجانًا" : `${p.price} جنيه`}
                              </td>

                              <td className="py-3 px-4">{p.durationDays}</td>
                              <td className="py-3 px-4">{p.trialDays || 0}</td>

                              <td className="py-3 px-4">
                                <div className="flex items-center justify-center gap-3">
                                  <button
                                    type="button"
                                    onClick={async () => {
                                      const newName = prompt("✏️ الاسم الجديد:", p.name);
                                      const newPrice = prompt("💰 السعر الجديد:", p.price);
                                      const newDays = prompt("📅 مدة الاشتراك بالأيام:", p.durationDays);
                                      const newTrial = prompt("🎁 الأيام المجانية:", p.trialDays || 0);

                                      if (!newName || newPrice === "" || !newDays) return;

                                      try {
                                        await api.put(`/settings/plans/${p._id}`, {
                                          name: newName,
                                          price: Number(newPrice),
                                          durationDays: Number(newDays),
                                          trialDays: Number(newTrial),
                                        });
                                        alert("✅ تم تعديل الخطة بنجاح!");
                                        fetchPlans();
                                      } catch (err) {
                                        console.error(err);
                                        alert("❌ حدث خطأ أثناء تعديل الخطة.");
                                      }
                                    }}
                                    className="bg-amber-400 hover:bg-amber-500 text-black px-4 py-1.5 rounded-lg font-semibold shadow-sm transition"
                                  >
                                    ✏️ تعديل
                                  </button>

                                  <button
                                    type="button"
                                    onClick={async () => {
                                      if (
                                        !window.confirm(
                                          `هل تريد حذف "${p.name}"${
                                            p.price === 0 ? " (الخطة المجانية)" : ""
                                          }?`
                                        )
                                      )
                                        return;

                                      try {
                                        await api.delete(`/settings/plans/${p._id}`);
                                        alert("🗑️ تم حذف الخطة بنجاح!");
                                        fetchPlans();
                                      } catch (err) {
                                        console.error(err);
                                        alert("❌ فشل حذف الخطة.");
                                      }
                                    }}
                                    className="bg-rose-500 hover:bg-rose-600 text-white px-4 py-1.5 rounded-lg font-semibold shadow-sm transition"
                                  >
                                    🗑️ حذف
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:hidden animate-stagger-parent">
                      {plans.map((p, i) => (
                        <div
                          key={p._id}
                          className={`border border-slate-200 rounded-2xl p-4 bg-white shadow-sm hover:shadow-md transition animate-card ${
                            p.price === 0 ? "ring-1 ring-green-200" : ""
                          }`}
                          style={{ animationDelay: `${i * 70}ms` }}
                        >
                          <div className="flex items-center justify-between mb-2">
                            <h3 className="font-extrabold text-slate-900">
                              {p.price === 0 ? "🎁 " : ""}
                              {p.name}
                            </h3>
                            <span
                              className={`text-xs px-2 py-1 rounded-lg ${
                                p.price === 0 ? "bg-green-50 text-green-700" : "bg-blue-50 text-blue-700"
                              }`}
                            >
                              {p.price === 0 ? "مجاني" : `${p.price} جنيه`}
                            </span>
                          </div>

                          <div className="text-[13px] space-y-1.5 text-slate-700">
                            <p>
                              <span className="font-semibold">⏳ المدة:</span> {p.durationDays} يوم
                            </p>
                            <p>
                              <span className="font-semibold">🎁 مجانًا:</span> {p.trialDays || 0} يوم
                            </p>
                          </div>

                          <div className="mt-3 flex gap-2">
                            <button
                              type="button"
                              onClick={async () => {
                                const newName = prompt("✏️ الاسم الجديد:", p.name);
                                const newPrice = prompt("💰 السعر الجديد:", p.price);
                                const newDays = prompt("📅 مدة الاشتراك بالأيام:", p.durationDays);
                                const newTrial = prompt("🎁 الأيام المجانية:", p.trialDays || 0);

                                if (!newName || newPrice === "" || !newDays) return;

                                try {
                                  await api.put(`/settings/plans/${p._id}`, {
                                    name: newName,
                                    price: Number(newPrice),
                                    durationDays: Number(newDays),
                                    trialDays: Number(newTrial),
                                  });
                                  alert("✅ تم تعديل الخطة بنجاح!");
                                  fetchPlans();
                                } catch (err) {
                                  console.error(err);
                                  alert("❌ حدث خطأ أثناء تعديل الخطة.");
                                }
                              }}
                              className="flex-1 bg-amber-400 hover:bg-amber-500 text-black px-4 py-2 rounded-lg font-semibold shadow-sm transition"
                            >
                              تعديل
                            </button>

                            <button
                              type="button"
                              onClick={async () => {
                                if (
                                  !window.confirm(
                                    `هل تريد حذف "${p.name}"${p.price === 0 ? " (الخطة المجانية)" : ""}?`
                                  )
                                )
                                  return;

                                try {
                                  await api.delete(`/settings/plans/${p._id}`);
                                  alert("🗑️ تم حذف الخطة بنجاح!");
                                  fetchPlans();
                                } catch (err) {
                                  console.error(err);
                                  alert("❌ فشل حذف الخطة.");
                                }
                              }}
                              className="flex-1 bg-rose-500 hover:bg-rose-600 text-white px-4 py-2 rounded-lg font-semibold shadow-sm transition"
                            >
                              حذف
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                ) : (
                  <p className="text-slate-500 text-center mt-6">
                    📭 لا توجد خطط حالياً. أضف خطة جديدة لبدء العمل.
                  </p>
                )}
              </div>
            </div>
          )}
        </div>
      </main>

      <style>{`
        .custom-scroll::-webkit-scrollbar { width: 8px; height: 8px; }
        .custom-scroll::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 9999px; }
        .custom-scroll::-webkit-scrollbar-thumb:hover { background: #94a3b8; }

        @keyframes sectionFadeUp {
          0% { opacity: 0; transform: translateY(8px); }
          100% { opacity: 1; transform: translateY(0); }
        }
        .animate-section { animation: sectionFadeUp .45s ease-out both; }

        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        .animate-fadeIn { animation: fadeIn .25s ease-out both; }

        @keyframes popIn {
          0% { opacity: 0; transform: translateY(8px) scale(0.98); }
          100% { opacity: 1; transform: translateY(0) scale(1); }
        }
        .animate-popIn { animation: popIn .25s ease-out both; }

        @keyframes cardIn {
          0% { opacity: 0; transform: translateY(14px); }
          100% { opacity: 1; transform: translateY(0); }
        }
        .animate-card { animation: cardIn .45s cubic-bezier(.2,.8,.2,1) both; }
        .animate-stagger-parent > * { will-change: transform, opacity; }
      `}</style>
    </div>
  );
}

export default AdminDashboard;
